import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { get, find, isArray, isEmpty, reverse, sortBy, filter, isNull } from 'lodash';
import { format } from 'date-fns';
import { Badge, Box, CircularProgress, Grid2, IconButton, Paper, Tooltip } from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LabelIcon from '@mui/icons-material/Label';
import { IDocumentDetail } from '../Patient/_types';
import { getDocument } from './_api';
import { getAllDocumentTags, setActiveDocumentTag } from '../Patient/_api';
import { getCommentNameWithLogin, joinParams } from 'utils/study';
import { useLanguage } from 'utils/hooks/useLanguage';
import { useActions } from 'utils/hooks/useActions';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { Line } from 'components/Line/Line';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import OverflowedDialog from 'components/Dialog/OverflowedDialog';
import { Papeer } from 'components/Papeer/Papeer';
import { DocumentDetailMenu } from './DocumentDetailMenu';
import { DICOMDocument } from '../Patient/DICOMDocument';
import { PDFDocument } from '../Patient/PDFDocument';
import { RTFDocument } from '../Patient/RTFDocument';

const formatDateWithoutTime = 'dd. MM. yyyy';
const formatDateTime = 'dd. MM. yyyy, HH:mm:ss';

export const DocumentDetail: React.FC<IDocumentDetail> = ({
  patientDocument,
  patientDocumentUuid,
  masterPatientID,
  unselectDocumentUuid,
  documentUids,
  patientDocuments,
  setDocumentToGridPosition,
  itemKey,
  onlyAddTag,
  setIsPrinting,
  setStandardizedPrint,
  setIsExporting,
  requestedDocuments,
  activeRegisterGroups,
  setActiveRegisterGroups,
  gridLayoutParams,
  selectingDocumentDisabled,
  setIsDownloadingFromEldax,
  facilities,
}) => {
  const { t } = useTranslation('ClinicalPortal');
  const { toggleLoader } = useAppGlobals();
  const { currentLocale } = useLanguage();
  const { storeConfirmationData } = useActions();
  const { confirmationData } = useAppInfo();

  const [isDicom, setIsDicom] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNotLoaded, setIsNotLoaded] = useState<boolean>(false);
  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const [documentTags, setDocumentTags] = useState<any[] | null>(null);
  const [documentUiid, setDocumentUiid] = useState<string | null>(null);
  const [document, setDocument] = useState<any>(null);
  const [documentType, setDocumentType] = useState<any>(null);
  const [documentTypeFormat, setDocumentTypeFormat] = useState<any>(null);
  const [documentExtension, setDocumentExtension] = useState<any>(null);
  const [documentHeader, setDocumentHeader] = useState<any>();
  const [RenderedDocumentTags, setRenderedDocumentTags] = useState<any>();
  const [documentFullScrenRender, setDocumentFullScrenRender] = useState<JSX.Element>();

  const patientDocumentJsonString = JSON.stringify(patientDocument);
  const documentTagsJsonString = JSON.stringify(documentTags);

  const isNotEmptyDocument =
    (document != null &&
      documentType != null &&
      documentTypeFormat != null &&
      !isNull(documentUiid)) ||
    documentExtension != null;

  const getDocumentTags = async (docUiid: any) => {
    if (docUiid || (docUiid === undefined && documentUiid)) {
      const documentTags = await getAllDocumentTags(docUiid || documentUiid);
      if (isArray(documentTags) && documentTags.length) {
        setDocumentTags(reverse(sortBy(filter(documentTags, ['active', true]), ['id'])));
      } else {
        setDocumentTags(null);
      }
    } else {
      setDocumentTags(null);
    }
  };

  const loadDocument = async (masterPatientID: any, documentUiid: any, typeOfDocument: any) => {
    let hasInitialized = true;
    if (masterPatientID !== null && documentUiid !== null && typeOfDocument !== null) {
      setIsLoading(true);
      const documentResponse = await getDocument(masterPatientID, documentUiid, typeOfDocument);

      if (documentResponse) {
        await getDocumentTags(documentUiid);
        setDocument(null);
        setDocumentUiid(documentUiid);
        setDocumentType(typeOfDocument);
        const documentData = get(documentResponse, 'data', null);
        if (documentData != null) {
          setDocumentTypeFormat(get(documentData, 'type', null));
          setDocument(get(documentData, 'content', {}));
        }

        const hospitalizationArray: string[] = [];
        const hospitalizationStart = get(documentResponse, 'hospitalization.start', null);
        const hospitalizationEnd = get(documentResponse, 'hospitalization.end', null);
        if (hospitalizationStart) {
          hospitalizationArray.push(
            `${t('hospitalizationFrom')} ${format(new Date(hospitalizationStart), formatDateTime)}`,
          );
        }
        if (hospitalizationEnd) {
          hospitalizationArray.push(
            `${t('hospitalizationTo')} ${format(new Date(hospitalizationEnd), formatDateTime)}`,
          );
        }

        const diagnosisArray: any[] = [];
        const diagnosis = get(documentResponse, 'diagnosis');
        if (isArray(diagnosis)) {
          diagnosis.forEach((item) => {
            const arrayDiagnosis: string[] = [];
            const code = get(item, 'code', '');
            const type = get(item, 'type', '');
            const description = get(item, 'description', '');
            const typeCode = get(item, 'typeCode', '');
            const typeDescription = get(item, 'typeDescription', '');
            const priorityCode = get(item, 'priorityCode', '');
            if (code) arrayDiagnosis.push(`${t('diagnosisCode')} ${code}`);
            if (type) arrayDiagnosis.push(`${t('diagnosisType')} ${type}`);
            if (description) arrayDiagnosis.push(`${t('diagnosisDescription')} ${description}`);
            if (typeCode) arrayDiagnosis.push(`${t('diagnosisTypeCode')} ${typeCode}`);
            if (typeDescription)
              arrayDiagnosis.push(`${t('diagnosisTypeDescription')} ${typeDescription}`);
            if (priorityCode) arrayDiagnosis.push(`${t('diagnosisPriorityCode')} ${priorityCode}`);
            diagnosisArray.push(joinParams(arrayDiagnosis, ' | '));
          });
        }

        const documentExtension = {
          ...(!isEmpty(diagnosisArray) ? { diagnosis: diagnosisArray } : {}),
          ...(!isEmpty(hospitalizationArray)
            ? { hospitalization: joinParams(hospitalizationArray) }
            : {}),
        };

        setDocumentExtension(documentExtension);
        hasInitialized = false;
        setIsNotLoaded(false);
      } else {
        setIsNotLoaded(true);
      }
      setIsLoading(false);
    }
    if (hasInitialized) {
      setDocument(null);
      setDocumentExtension(null);
      setDocumentUiid(null);
      setDocumentType(null);
      setDocumentTypeFormat(null);
      getDocumentTags(null);
    }
  };

  const getIsDicom = (allowedTypes: any) => {
    setIsDicom(
      find(allowedTypes, {
        name: 'dicom',
      }) !== undefined,
    );
  };

  const loadEntity = async () => {
    getIsDicom(get(patientDocument, 'typeOfDocument.allowedFormats', []));
    await loadDocument(
      masterPatientID,
      get(patientDocument, 'uuid', null),
      get(patientDocument, 'typeOfDocument.name', null),
    );
  };

  useEffect(() => {
    loadEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDocumentUuid]);

  useEffect(() => {
    const externalWorkplaceName = get(patientDocument, 'externalWorkplaceName');
    setDocumentHeader([
      {
        name: 'patient',
        value: `${joinParams([
          get(patientDocument, 'patientEntity.prefix'),
          get(patientDocument, 'patientEntity.lastName'),
          get(patientDocument, 'patientEntity.firstName'),
          get(patientDocument, 'patientEntity.middleName'),
          get(patientDocument, 'patientEntity.suffix'),
          `(${t('patientIdentificationNumber')}:`,
          get(patientDocument, 'patientEntity.identificationNumber', '-') != null
            ? get(patientDocument, 'patientEntity.identificationNumber', '-')
            : '-',
          ' | ',
          `${t('patientBirthDate')}:`,
          get(patientDocument, 'patientEntity.birthDate', '')
            ? format(
                new Date(get(patientDocument, 'patientEntity.birthDate', '')),
                formatDateWithoutTime,
              )
            : '',
          ')',
        ])}`,
        fontWeight: 'bold',
      },
      {
        name: 'nameOfdocument',
        value: `${joinParams([
          get(patientDocument, 'typeOfDocument.translateJson')
            ? get(
                JSON.parse(get(patientDocument, 'typeOfDocument.translateJson')),
                currentLocale,
                get(patientDocument, 'typeOfDocument.name'),
              )
            : `${t('unknownNameOfDocument')}`,
        ])}
        `,
        fontWeight: 'bold',
        fontSize: 'large',
      },
      {
        name: 'name',
        value: `${joinParams([
          `(${t('createdDateTime')}:`,
          get(patientDocument, 'createdDateTime') != null
            ? format(new Date(get(patientDocument, 'createdDateTime', '')), formatDateWithoutTime)
            : `${t('unknownNameDate')}`,
          ` | ${t('author')}:`,
          get(patientDocument, 'author') != null
            ? get(patientDocument, 'author')
            : `${t('unknownAuthorOfDocument')}`,
          ` | ${t('referenceID')}:`,
          get(patientDocument, 'referenceID') != null
            ? get(patientDocument, 'referenceID')
            : `${t('unknownReferenceOfDocument')}`,
          ` | ${t('externalWorkplaceName')}:`,
          externalWorkplaceName != null
            ? externalWorkplaceName
            : `${t('unknownExternalWorkplaceName')}`,
          ')',
        ])}
        `,
        lg: 6,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDocumentJsonString]);

  useEffect(() => {
    setRenderedDocumentTags(
      isArray(documentTags) && documentTags.length
        ? documentTags.map((documentTag, index) => {
            const createdByAuthor = getCommentNameWithLogin(get(documentTag, 'createdBy'));
            const canDelete = true;
            return (
              <Box sx={{ display: 'inline-flex', pr: 3 }} key={`${documentTag.id}_${index}`}>
                {documentTag.value.split('*^*').map((item: string, index: number) => {
                  let j = 0;
                  return (
                    <Tooltip
                      title={
                        <div>
                          {createdByAuthor} -{' '}
                          {documentTag.createdWhen
                            ? format(new Date(documentTag.createdWhen), formatDateTime)
                            : ''}
                        </div>
                      }
                      key={`${item}_${index}`}
                    >
                      <Badge
                        sx={{
                          mr: 1.5,
                          '.MuiBadge-badge': {
                            right: -8,
                            top: 3,
                          },
                        }}
                        badgeContent={
                          canDelete ? (
                            <Tooltip
                              key="documentTagDelete"
                              title={t('documentTagDelete')}
                              placement="top"
                            >
                              <IconButton
                                sx={{
                                  mr: 1.5,
                                  '.MuiIconButton-root': {
                                    padding: 0,
                                    margin: 0,
                                    top: '25%',
                                  },
                                }}
                                onClick={() => storeConfirmationData({ id: documentTag.id })}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            0
                          )
                        }
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            border: '1px solid #ccc',
                            borderRadius: 20,
                            marginTop: 0.5,
                          }}
                        >
                          <LabelIcon sx={{ fontSize: 20, color: '#676a6c', mr: 1, ml: 1 }} />
                          {item
                            .substr(1)
                            .split('#')
                            .map((tag: string) => {
                              j++;
                              return (
                                <Box
                                  component="span"
                                  key={tag}
                                  sx={{ display: 'inline-flex', mr: 1 }}
                                >
                                  {j > 1 && <KeyboardArrowRightIcon />}
                                  {tag}
                                </Box>
                              );
                            })}
                        </Box>
                      </Badge>
                    </Tooltip>
                  );
                })}
              </Box>
            );
          })
        : '',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTagsJsonString]);

  const showBigPreview = (value: boolean) => {
    setFullScreen(value);
  };

  const documentRender = (
    <Papeer>
      <Box id="document">
        <Grid2 container={true} spacing={2}>
          {isNotEmptyDocument ? (
            <Grid2 key={'header'} size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  {documentHeader.map((field: any, index: number) => (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: get(field, 'fontWeight', 'normal'),
                        fontSize: get(field, 'fontSize', 'normal'),
                      }}
                      key={`${field.value}_${index}`}
                    >
                      {field.value}
                    </Box>
                  ))}
                  {RenderedDocumentTags}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    onClick={() => {
                      toggleLoader();
                      showBigPreview(!fullScreen);
                    }}
                  >
                    {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                  {!fullScreen && (
                    <DocumentDetailMenu
                      itemKey={itemKey}
                      documentUiid={documentUiid}
                      documentUids={documentUids}
                      masterPatientID={masterPatientID}
                      unselectDocumentUuid={unselectDocumentUuid}
                      patientDocuments={patientDocuments}
                      setDocumentToGridPosition={setDocumentToGridPosition}
                      patientDocument={patientDocument}
                      getDocumentTags={getDocumentTags}
                      onlyAddTag={onlyAddTag}
                      setIsPrinting={setIsPrinting}
                      setStandardizedPrint={setStandardizedPrint}
                      setIsExporting={setIsExporting}
                      activeRegisterGroups={activeRegisterGroups}
                      setActiveRegisterGroups={setActiveRegisterGroups}
                      gridLayoutParams={gridLayoutParams}
                      selectingDocumentDisabled={selectingDocumentDisabled}
                      setIsDownloadingFromEldax={setIsDownloadingFromEldax}
                      facilities={facilities}
                    />
                  )}
                </Box>
              </Box>
            </Grid2>
          ) : (
            <Grid2 key="unknownDocument" size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', m: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {t(isNotLoaded ? 'notLoadedDocument' : 'unknownDocument')}
                </Box>
                <DocumentDetailMenu
                  itemKey={itemKey}
                  documentUiid={documentUiid}
                  documentUids={documentUids}
                  masterPatientID={masterPatientID}
                  patientDocuments={patientDocuments}
                  setDocumentToGridPosition={setDocumentToGridPosition}
                  menuStyle={{}}
                  patientDocument={patientDocument}
                  onlyAddTag={onlyAddTag}
                  setIsPrinting={setIsPrinting}
                  setStandardizedPrint={setStandardizedPrint}
                  setIsExporting={setIsExporting}
                  activeRegisterGroups={activeRegisterGroups}
                  setActiveRegisterGroups={setActiveRegisterGroups}
                  gridLayoutParams={gridLayoutParams}
                  selectingDocumentDisabled={selectingDocumentDisabled}
                  setIsDownloadingFromEldax={setIsDownloadingFromEldax}
                  facilities={facilities}
                />
              </Box>
            </Grid2>
          )}
        </Grid2>
      </Box>
      <Line topMargin={1} bottomMargin={2} />
      {documentExtension != null &&
        (!isEmpty(get(documentExtension, 'diagnosis')) ||
          !isEmpty(get(documentExtension, 'hospitalization'))) && (
          <Box>
            {!isEmpty(get(documentExtension, 'hospitalization')) && (
              <div>{get(documentExtension, 'hospitalization')}</div>
            )}
            {isArray(get(documentExtension, 'diagnosis')) &&
              !isEmpty(get(documentExtension, 'diagnosis')) &&
              get(documentExtension, 'diagnosis').map((item: any, index: number) => (
                <div key={index}>{item}</div>
              ))}
          </Box>
        )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: new RegExp('application/pdf').test(documentTypeFormat)
            ? 'center'
            : 'left',
          alignItems: 'center',
          overflow: new RegExp('application/json').test(documentTypeFormat) ? 'auto' : 'unset',
        }}
      >
        {document != null && documentType != null && documentTypeFormat != null && (
          <div
            style={{
              maxWidth: isDicom ? 'none' : '-webkit-fill-available',
              width: isDicom ? '100%' : undefined,
            }}
          >
            {isDicom === true ? (
              <>
                {new RegExp('application/json').test(documentTypeFormat) && (
                  <Box sx={{ width: '100%' }}>
                    <DICOMDocument document={document} />
                  </Box>
                )}
              </>
            ) : (
              <>
                {new RegExp('application/pdf').test(documentTypeFormat) && (
                  <PDFDocument document={document} />
                )}
                {new RegExp('text/plain').test(documentTypeFormat) && (
                  <Box sx={{ maxWidth: '100%', overflow: 'auto' }}>
                    <Box
                      component="span"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        display: 'block',
                      }}
                    >
                      {document}
                    </Box>
                  </Box>
                )}
                {new RegExp('text/rtf').test(documentTypeFormat) && (
                  <RTFDocument document={document} />
                )}
              </>
            )}
          </div>
        )}
      </Box>
    </Papeer>
  );

  useEffect(() => {
    const documentFullScrenRender = (
      <OverflowedDialog
        key={'fullScreenPreview'}
        fullScreen={true}
        open={fullScreen}
        onClose={setFullScreen}
        aria-labelledby="form-dialog-title"
        withScrolling={true}
        contentStyle={{ padding: 0 }}
      >
        {documentRender}
      </OverflowedDialog>
    );
    setDocumentFullScrenRender(documentFullScrenRender);
    toggleLoader(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScreen]);

  const onChangeActive = async (id: number) => {
    toggleLoader();
    const changed = await setActiveDocumentTag(id, false);
    if (changed) {
      await getDocumentTags(documentUiid);
    }
    toggleLoader(false);
  };
  const clearAction = () => {
    storeConfirmationData(null);
  };

  const cancelAction = () => {
    clearAction();
  };
  const confirmAction = async () => {
    onChangeActive(get(confirmationData, 'id'));
    clearAction();
  };

  return (
    <>
      <Paper
        sx={{
          position: 'relative',
          width: '100%',
          ...(isLoading || !isNotEmptyDocument ? { height: 300 } : {}),
          overflow: 'auto',
        }}
      >
        {isLoading && (
          <Box
            sx={{
              background: 'rgba(255,255,255,0.8)',
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={50} />
          </Box>
        )}

        {!isLoading ? (
          documentRender
        ) : (
          <div id="document">
            <Grid2 container={true} spacing={16} style={{ marginTop: 8 }}>
              <Grid2 size={{ xs: 12 }}>
                <div style={{ margin: 8 }}>{t('loadingData')}</div>
              </Grid2>
            </Grid2>
          </div>
        )}

        {confirmationData && confirmationData.id && (
          <ConfirmationDialog
            title={t('confirmDelete')}
            open={true}
            aria-labelledby="form-dialog-title"
            fullWidth={true}
            cancelAction={cancelAction}
            confirmAction={confirmAction}
          />
        )}
        {documentFullScrenRender}
      </Paper>
    </>
  );
};
